import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { vcardService } from '../../services/vcardService';
import { getAppearanceStyles } from '../../utils/appearance';

export const CardPreview = ({ card, links, mode = 'preview', vcardUrl, onSaveContact, onExchangeContact, onTrack }) => {
  const s = getAppearanceStyles(card.appearance);
  
  const vis = card.contact_visibility || { email: true, phone: true, website: true, location: true };
  const actions = card.actions_config || { save_contact: true, exchange_contact: false };
  const visibleLinks = links.filter(l => l.is_visible);

  let customActions = actions.items || [];
  if (!actions.items) {
    if (actions.book_meeting?.enabled) {
      customActions.push({ id: 'lb', type: 'booking', label: actions.book_meeting.label || 'Book a Meeting', url: actions.book_meeting.url, is_visible: true });
    }
    if (actions.custom_cta?.enabled) {
      customActions.push({ id: 'lc', type: 'custom', label: actions.custom_cta.label || 'Visit Website', url: actions.custom_cta.url, is_visible: true });
    }
  }
  const visibleCustomActions = customActions.filter(a => a.is_visible);

  const cardClass = `w-full h-full flex flex-col relative ${s.cardBg} ${s.fontClass} ${s.textMain} ${mode === 'preview' ? 'min-h-[600px] overflow-y-auto hide-scrollbar' + ' ' + s.cornerCard : 'min-h-screen sm:min-h-[800px]'}`;

  const handleActionClick = (e, actionType) => {
    if (mode === 'preview' && actionType !== 'save' && actionType !== 'exchange') {
      e.preventDefault();
      return;
    }
    if (actionType === 'save' && mode === 'preview') {
      e.preventDefault();
      vcardService.generateAndDownload(card);
      const btn = e.currentTarget;
      const originalText = btn.innerText;
      btn.innerText = 'Contact Saved! (Preview)';
      setTimeout(() => btn.innerText = originalText, 2000);
      return;
    }
    if (actionType === 'exchange') {
      e.preventDefault();
      if (mode === 'preview') {
        alert('Share Your Contact form will open here. Preview submissions are disabled.');
      } else if (onExchangeContact) {
        onExchangeContact();
      }
    }
  };

  const formatUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  const ContactWrapper = ({ href, children, className, onClick }) => {
    if (mode === 'public' && href) {
      return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} className={`${className} hover:opacity-80 transition-opacity`}>{children}</a>;
    }
    return <div className={className} onClick={e => mode === 'preview' && e.preventDefault()}>{children}</div>;
  };

  const content = (
    <div className={cardClass}>
      <div className="w-full h-32 shrink-0 opacity-20" style={{ backgroundColor: s.accentHex }}></div>
      <div className={`${s.px} ${s.pb} -mt-16 flex-1 flex flex-col`}>
        
        <div className={`${s.avatarSize} ${s.cornerAvatar} overflow-hidden border-4 ${s.border} ${s.cardBg} mx-auto z-10 shrink-0`}>
          {card.profile_image_url ? (
            <img src={card.profile_image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${s.textMuted}`}>
              <SafeIcon icon={FiIcons.FiUser} className="text-4xl" />
            </div>
          )}
        </div>

        <div className={`text-center mt-4 mb-6`}>
          <h2 className={`text-2xl font-bold ${s.textMain}`}>{card.full_name || 'Your Name'}</h2>
          {card.pronouns && <p className={`text-sm ${s.textMuted} mt-1`}>{card.pronouns}</p>}
          <p className={`text-base font-medium mt-1 ${s.textMain}`}>{card.job_title || 'Job Title'}</p>
          {card.company_name && <p className={`text-sm ${s.textMuted}`}>{card.company_name}</p>}
          {vis.location && card.location && (
            <p className={`text-xs ${s.textMuted} mt-2 flex items-center justify-center`}>
              <SafeIcon icon={FiIcons.FiMapPin} className="mr-1" />{card.location}
            </p>
          )}
        </div>

        {card.bio && (
          <p className={`text-sm text-center mb-8 ${s.textMuted} whitespace-pre-wrap leading-relaxed`}>{card.bio}</p>
        )}

        <div className={`${s.gap} mb-8`}>
          {visibleCustomActions.map((action, i) => (
            <a
              key={action.id || i}
              href={formatUrl(action.url)}
              target={mode === 'preview' ? '_self' : '_blank'}
              rel="noreferrer"
              onClick={(e) => {
                handleActionClick(e, 'custom');
                onTrack?.('cta_clicked', { cta_type: action.type, cta_label: action.label, url: action.url });
              }}
              className={`w-full ${s.py} px-4 flex justify-center font-bold text-sm transition-opacity hover:opacity-90 ${s.cornerButton}`}
              style={{ backgroundColor: s.accentHex, color: '#ffffff' }}
            >
              {action.label || 'Action'}
            </a>
          ))}

          {actions.exchange_contact && (
            <button
              onClick={(e) => handleActionClick(e, 'exchange')}
              className={`w-full ${s.py} px-4 flex justify-center font-bold text-sm transition-opacity hover:opacity-90 ${s.cornerButton}`}
              style={{ backgroundColor: s.accentHex, color: '#ffffff' }}
            >
              Share Your Contact
            </button>
          )}

          {actions.save_contact && mode === 'public' && (
            <a
              href={vcardUrl || '#'}
              download={`${card.public_slug}.vcf`}
              onClick={(e) => {
                if (!vcardUrl) {
                  e.preventDefault();
                } else if (onSaveContact) {
                  onSaveContact();
                }
              }}
              className={`w-full ${s.py} px-4 flex justify-center font-bold text-sm transition-opacity hover:opacity-90 ${s.cornerButton}`}
              style={{ backgroundColor: s.accentHex, color: '#ffffff' }}
            >
              Save Contact
            </a>
          )}

          {actions.save_contact && mode === 'preview' && (
            <button
              onClick={(e) => handleActionClick(e, 'save')}
              className={`w-full ${s.py} px-4 flex justify-center font-bold text-sm transition-opacity hover:opacity-90 ${s.cornerButton}`}
              style={{ backgroundColor: s.accentHex, color: '#ffffff' }}
            >
              Save Contact
            </button>
          )}
        </div>

        <div className={`${s.gap} mt-auto`}>
          {vis.email && card.email && (
            <ContactWrapper href={`mailto:${card.email}`} onClick={() => onTrack?.('email_clicked')} className={`${s.py} px-4 flex items-center ${s.elementBg} ${s.elementHover} ${s.cornerElement} transition-colors cursor-pointer`}>
              <SafeIcon icon={FiIcons.FiMail} className={`mr-3 ${s.textMuted}`} />
              <span className={`text-sm font-medium ${s.textMain} truncate`}>{card.email}</span>
            </ContactWrapper>
          )}
          
          {vis.phone && card.phone && (
            <ContactWrapper href={`tel:${card.phone}`} onClick={() => onTrack?.('phone_clicked')} className={`${s.py} px-4 flex items-center ${s.elementBg} ${s.elementHover} ${s.cornerElement} transition-colors cursor-pointer`}>
              <SafeIcon icon={FiIcons.FiPhone} className={`mr-3 ${s.textMuted}`} />
              <span className={`text-sm font-medium ${s.textMain}`}>{card.phone}</span>
            </ContactWrapper>
          )}
          
          {vis.website && card.website && (
            <ContactWrapper href={formatUrl(card.website)} onClick={() => onTrack?.('website_clicked')} className={`${s.py} px-4 flex items-center ${s.elementBg} ${s.elementHover} ${s.cornerElement} transition-colors cursor-pointer`}>
              <SafeIcon icon={FiIcons.FiGlobe} className={`mr-3 ${s.textMuted}`} />
              <span className={`text-sm font-medium ${s.textMain} truncate`}>{card.website}</span>
            </ContactWrapper>
          )}
          
          {visibleLinks.map((l, i) => {
            const isLinkedIn = l.url.toLowerCase().includes('linkedin.com');
            const eventName = isLinkedIn ? 'linkedin_clicked' : 'link_clicked';
            return (
              <a
                key={i}
                href={formatUrl(l.url)}
                target={mode === 'preview' ? "_self" : "_blank"}
                rel="noreferrer"
                onClick={(e) => {
                  if (mode === 'preview') e.preventDefault();
                  else onTrack?.(eventName, { url: l.url, platform: l.platform });
                }}
                className={`${s.py} px-4 flex items-center border ${s.border} ${s.elementBg} ${s.elementHover} ${s.cornerElement} transition-colors`}
              >
                <SafeIcon icon={FiIcons.FiLink} className={`mr-3`} style={{ color: s.accentHex }} />
                <span className={`text-sm font-medium ${s.textMain} truncate`}>{l.title || 'Link'}</span>
                <SafeIcon icon={FiIcons.FiExternalLink} className={`ml-auto text-xs ${s.textMuted}`} />
              </a>
            );
          })}
        </div>

        <div className="mt-8 pt-6 pb-4 text-center shrink-0">
          <span className={`text-[10px] uppercase tracking-widest font-bold opacity-40 ${s.textMain}`}>
            Made with Meetoi
          </span>
        </div>
      </div>
    </div>
  );

  if (mode === 'public') {
    return content;
  }

  return (
    <div className={`w-full max-w-[340px] mx-auto ${s.cardBg} ${s.cornerCard} shadow-xl border-[12px] border-slate-900 overflow-hidden relative aspect-[9/19]`}>
      {content}
    </div>
  );
};